import React, { useState, useEffect, useRef, useContext } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPost, avisaError, dinero, mensajeError, llave, variante } from "./posApi";
import VariantePicker from "./VariantePicker";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import { ticketVenta } from "./posTickets";
import SinPermiso from "./SinPermiso";
import FotoModelo from "../Articulos/FotoModelo";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function PosVenta() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(false);
  const { tienda } = ctx;

  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState([]);
  const [sinCoincidencias, setSinCoincidencias] = useState(false);
  const [marcado, setMarcado] = useState(-1); // resultado elegido con flechas
  const consulta = useRef(0); // descarta respuestas viejas cuando ya se escribió otra cosa
  const [buscando, setBuscando] = useState(false);
  const [carrito, setCarrito] = useState([]); // [{k, articulos, colores, tallas, color, talla, codigo, nombre, cantidad, existencia}]
  const [picker, setPicker] = useState(null); // modelo con tallas/colores para elegir
  const [cotizacion, setCotizacion] = useState(null);
  const [cotizando, setCotizando] = useState(false);
  const [modalCobro, setModalCobro] = useState(false);
  const [pago, setPago] = useState({ efectivo: "", tdc: "", transfer: "", referenciaTdc: "", referenciaTransfer: "" });
  const [cliente, setCliente] = useState("");
  const [guardando, setGuardando] = useState(false);
  const inputBusqueda = useRef(null);
  // Vender sin existencia: solo con permiso y confirmando una vez por talla y color en cada venta
  const puedeSinEx = !!(user && user.posVenderSinExistencia);
  const carritoRef = useRef([]);
  const sinExOk = useRef({});
  useEffect(() => { carritoRef.current = carrito; }, [carrito]);
  const timer = useRef(null);

  const enfocar = () => setTimeout(() => inputBusqueda.current && inputBusqueda.current.focus(), 50);

  // Recalcula precios y promociones en el servidor cada vez que cambia el carrito
  useEffect(() => {
    if (!tienda || !carrito.length) { setCotizacion(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setCotizando(true);
      posPost("/cotizar", { area: tienda, renglones: carrito.map((c) => ({ articulos: c.articulos, colores: c.colores, tallas: c.tallas, cantidad: c.cantidad })) })
        .then(setCotizacion)
        .catch((e) => { setCotizacion(null); avisaError(e); })
        .finally(() => setCotizando(false));
    }, 250);
  }, [carrito, tienda]);

  // Atajo F2 = cobrar
  useEffect(() => {
    const tecla = (e) => { if (e.key === "F2") { e.preventDefault(); abrirCobro(); } };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  });

  useEffect(() => { enfocar(); }, [tienda]);

  // Agrega piezas de una variante al carrito; sin pasar de lo que hay, salvo permiso de vender sin existencia
  const agregarVariante = async (a, v, cantidad = 1) => {
    const k = llave(a._id, v.colores, v.tallas);
    const ex = ((a.variantes || []).find((x) => llave(a._id, x.colores, x.tallas) === k) || {}).existencia || 0;
    const ya = carritoRef.current.find((c) => c.k === k);
    if ((ya ? ya.cantidad : 0) + cantidad > ex) {
      if (!puedeSinEx) {
        Swal.fire({ icon: "warning", title: "Sin existencia", text: `${a.codigo} ${variante(v)}: hay ${ex} pieza(s) en la tienda`, timer: 2200, showConfirmButton: false });
        return;
      }
      if (!sinExOk.current[k]) {
        const r = await Swal.fire({
          icon: "warning", title: "No hay existencia en sistema",
          html: `${a.codigo} ${variante(v)}: el sistema dice que hay <b>${ex}</b>.<br/><br/>¿La tienes en la mano y la vendes de todos modos?<br/><small>El inventario queda en negativo hasta que se dé la entrada.</small>`,
          showCancelButton: true, confirmButtonText: "Sí, vender", cancelButtonText: "No", focusCancel: true,
        });
        if (!r.value) { enfocar(); return; }
        sinExOk.current[k] = true;
      }
    }
    setCarrito((prev) => {
      const y = prev.find((c) => c.k === k);
      if (y) return prev.map((c) => (c.k === k ? { ...c, cantidad: c.cantidad + cantidad, existencia: ex } : c));
      return [...prev, { k, articulos: a._id, colores: v.colores, tallas: v.tallas, color: v.color, talla: v.talla, codigo: a.codigo, nombre: a.nombre, cantidad, existencia: ex }];
    });
    enfocar();
  };

  const agregar = (a) => {
    consulta.current++;
    setBusqueda("");
    setResultados([]); setSinCoincidencias(false); setMarcado(-1);
    if (a.seleccion) { agregarVariante(a, a.seleccion); return; } // escanearon la etiqueta de una talla y color
    if (a.conVariantes) { setPicker(a); return; }
    agregarVariante(a, a.variantes[0] || { colores: null, tallas: null });
    enfocar();
  };

  // Mientras escribe: sugerencias sin agregar nada (el lector de código termina con Enter)
  useEffect(() => {
    const q = busqueda.trim();
    if (q.length < 2 || !tienda) { setResultados([]); setSinCoincidencias(false); setMarcado(-1); return undefined; }
    const id = ++consulta.current;
    const t = setTimeout(async () => {
      try {
        const r = await posGet("/articulos", { area: tienda, q });
        if (id !== consulta.current) return;
        setResultados(r); setSinCoincidencias(!r.length); setMarcado(-1);
      } catch (e) { /* al dar Enter se muestra el error */ }
    }, 250);
    return () => clearTimeout(t);
  }, [busqueda, tienda]);

  const buscar = async (texto) => {
    const q = (texto || "").trim();
    if (!q || !tienda) return;
    // Enter con un resultado elegido con flechas: se agrega ese
    if (marcado >= 0 && resultados[marcado]) return agregar(resultados[marcado]);
    const id = ++consulta.current;
    setBuscando(true);
    try {
      const r = await posGet("/articulos", { area: tienda, q });
      if (id !== consulta.current) return;
      // Código o EAN exacto (lector de código de barras): se agrega directo
      if (r.length === 1 && r[0].exacto) return agregar(r[0]);
      if (!r.length) Swal.fire({ icon: "info", title: "No encontrado", text: `No hay artículos con "${q}"`, timer: 1800, showConfirmButton: false });
      setResultados(r);
    } catch (e) {
      avisaError(e);
    } finally {
      setBuscando(false);
    }
  };

  const cambiaCantidad = (id, cant) => {
    setCarrito((prev) => prev.map((c) => {
      if (c.k !== id) return c;
      const n = Math.max(1, parseInt(cant, 10) || 1);
      if (n > c.existencia && puedeSinEx && sinExOk.current[c.k]) return { ...c, cantidad: n };
      if (n > c.existencia) {
        Swal.fire({ icon: "warning", title: "Sin existencia", text: `Solo hay ${c.existencia} pieza(s)`, timer: 1800, showConfirmButton: false });
        return { ...c, cantidad: c.existencia };
      }
      return { ...c, cantidad: n };
    }));
  };
  const quitar = (id) => { setCarrito((prev) => prev.filter((c) => c.k !== id)); enfocar(); };

  const nuevaVenta = () => {
    setCarrito([]); setCotizacion(null); setCliente(""); sinExOk.current = {};
    setPago({ efectivo: "", tdc: "", transfer: "", referenciaTdc: "", referenciaTransfer: "" });
    setModalCobro(false); enfocar();
  };

  const total = cotizacion ? cotizacion.total : 0;
  const pagado = r2((Number(pago.efectivo) || 0) + (Number(pago.tdc) || 0) + (Number(pago.transfer) || 0));
  const falta = r2(Math.max(total - pagado, 0));
  const noEfectivo = r2((Number(pago.tdc) || 0) + (Number(pago.transfer) || 0));
  const cambio = r2(Math.max(pagado - total, 0));
  const errorPago = noEfectivo > total ? "Tarjeta + transferencia no puede pasar del total" : "";

  function abrirCobro() {
    if (!carrito.length || !cotizacion || cotizando) return;
    setPago({ efectivo: "", tdc: "", transfer: "", referenciaTdc: "", referenciaTransfer: "" });
    setModalCobro(true);
  }

  const todoEn = (metodo) => setPago((p) => ({ ...p, efectivo: "", tdc: "", transfer: "", [metodo]: String(total) }));
  const restoEn = (metodo) => setPago((p) => ({ ...p, [metodo]: String(r2((Number(p[metodo]) || 0) + falta)) }));

  const cobrar = async () => {
    if (guardando || falta > 0 || errorPago) return;
    setGuardando(true);
    try {
      const venta = await posPost("/ventas", {
        area: tienda, cliente,
        renglones: carrito.map((c) => ({ articulos: c.articulos, colores: c.colores, tallas: c.tallas, cantidad: c.cantidad })),
        pagos: pago, sinExistencia: carrito.some((c) => c.cantidad > c.existencia),
      });
      ticketVenta(venta);
      await Swal.fire({
        icon: "success", title: `Venta ${venta.folio} registrada`,
        html: venta.cambio > 0 ? `<div style="font-size:1.6rem;font-weight:800">Cambio: ${dinero(venta.cambio)}</div>` : "",
        confirmButtonText: "Nueva venta",
      });
      nuevaVenta();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se registró la venta", text: mensajeError(e) });
    } finally {
      setGuardando(false);
    }
  };

  const filas = cotizacion ? cotizacion.items : [];
  const filaDe = (k) => filas.find((f) => llave(f.articulos, f.colores, f.tallas) === k);

  if (!(user && user.rolTiendas && user.posVender)) return <SinPermiso que="vender" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-cash-register mr-2" style={{ color: "#10b981" }} />Caja</h3>
            <p className="page-subtitle">Escanea o escribe el código y presiona Enter · F2 para cobrar</p>
          </div>
          <div className="page-actions">
            <TiendaSelector ctx={ctx} />
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        {!tienda ? (
          <div className="alert alert-warning">Selecciona una tienda para empezar a vender.</div>
        ) : (
          <div className="row">
            {/* Captura + carrito */}
            <div className="col-lg-8 mb-3">
              <div style={{ position: "relative" }}>
                <form onSubmit={(e) => { e.preventDefault(); buscar(busqueda); }} style={{ display: "flex", gap: 8 }}>
                  <input
                    ref={inputBusqueda}
                    className="form-control"
                    style={{ fontSize: "1.1rem", height: 48 }}
                    placeholder="Código, código de barras o nombre del modelo…"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { setResultados([]); setBusqueda(""); }
                      else if (e.key === "ArrowDown" && resultados.length) { e.preventDefault(); setMarcado((m) => Math.min(m + 1, resultados.length - 1)); }
                      else if (e.key === "ArrowUp" && resultados.length) { e.preventDefault(); setMarcado((m) => Math.max(m - 1, -1)); }
                    }}
                    onBlur={() => setTimeout(() => { setResultados([]); setSinCoincidencias(false); }, 200)}
                    autoComplete="off"
                  />
                  <Button color="primary" type="submit" disabled={buscando} style={{ minWidth: 110 }}>
                    {buscando ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-search mr-1" />Buscar</>}
                  </Button>
                </form>
                {resultados.length > 0 && (
                  <div style={{ position: "absolute", zIndex: 20, left: 0, right: 0, top: 54, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,.12)", maxHeight: 360, overflowY: "auto" }}>
                    {resultados.map((a, i) => (
                      <button
                        key={a._id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setMarcado(i)}
                        onClick={() => agregar(a)}
                        disabled={a.existencia <= 0 && !puedeSinEx}
                        style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: "none", borderBottom: "1px solid #f1f5f9", background: marcado === i ? "#eff6ff" : "transparent", textAlign: "left", opacity: a.existencia > 0 || puedeSinEx ? 1 : 0.45, cursor: a.existencia > 0 || puedeSinEx ? "pointer" : "not-allowed" }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 10 }}><FotoModelo url={a.urlFoto} size={34} ampliar={false} /><span><strong>{a.codigo}</strong> · {a.nombre}</span></span>
                        <span style={{ whiteSpace: "nowrap" }}>
                          <span className={`badge badge-${a.existencia > 0 ? "success" : "secondary"} mr-2`}>{a.existencia} pzs</span>
                          <strong>{dinero(a.precioTienda)}</strong>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {!resultados.length && sinCoincidencias && busqueda.trim().length >= 2 && (
                  <div style={{ position: "absolute", zIndex: 20, left: 0, right: 0, top: 54, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", color: "#64748b" }}>
                    Sin coincidencias para "{busqueda.trim()}"
                  </div>
                )}
              </div>

              <div className="table-responsive mt-3">
                <table className="table table-sm table-dark-header" style={{ fontSize: "0.9rem" }}>
                  <thead>
                    <tr>
                      <th>Modelo</th>
                      <th style={{ width: 130 }} className="text-center">Cantidad</th>
                      <th className="text-right">Importe</th>
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {carrito.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-muted py-5"><i className="fas fa-barcode fa-2x mb-2 d-block" />Escanea el primer artículo</td></tr>
                    )}
                    {carrito.map((c) => {
                      const f = filaDe(c.k);
                      return (
                        <tr key={c.k}>
                          <td>
                            <strong>{c.codigo}</strong> {c.nombre}
                            {(c.color || c.talla) && <div><span className="badge badge-light" style={{ border: "1px solid #e2e8f0", fontSize: "0.78rem" }}>{variante(c)}</span></div>}
                            {c.cantidad > c.existencia && (
                              <div><span className="badge" style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" }} title="El inventario quedará en negativo">
                                <i className="fas fa-exclamation-triangle mr-1" />{c.cantidad - Math.max(c.existencia, 0)} sin existencia
                              </span></div>
                            )}
                            {f && f.promociones.length > 0 && (
                              <div>{f.promociones.map((p) => <span key={p} className="badge badge-warning mr-1">{p}</span>)}</div>
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
                              <Button size="sm" color="secondary" onClick={() => cambiaCantidad(c.k, c.cantidad - 1)}>−</Button>
                              <Input bsSize="sm" style={{ width: 52, textAlign: "center" }} value={c.cantidad} onChange={(e) => cambiaCantidad(c.k, e.target.value)} />
                              <Button size="sm" color="secondary" onClick={() => cambiaCantidad(c.k, c.cantidad + 1)}>+</Button>
                            </div>
                          </td>
                          <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                            <strong>{f ? dinero(f.importe) : "…"}</strong>
                            {f && <div className="small text-muted">{c.cantidad} x {dinero(f.precioLista)}</div>}
                            {f && f.descuento > 0 && <div className="small" style={{ color: "#dc2626" }}>-{dinero(f.descuento)}</div>}
                          </td>
                          <td><Button size="sm" color="danger" onClick={() => quitar(c.k)} title="Quitar"><i className="fas fa-times" /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totales */}
            <div className="col-lg-4 mb-3">
              <div className="form-section" style={{ position: "sticky", top: 10 }}>
                <div className="form-section-header dark"><i className="fas fa-receipt" /><h4 className="form-section-title">Total de la venta</h4></div>
                <div className="form-section-body">
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b" }}>
                    <span>Piezas</span><strong>{carrito.reduce((t, c) => t + c.cantidad, 0)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b" }}>
                    <span>Subtotal</span><span>{dinero(cotizacion ? cotizacion.subtotal : 0)}</span>
                  </div>
                  {cotizacion && cotizacion.descuento > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#dc2626", fontWeight: 600 }}>
                      <span><i className="fas fa-tags mr-1" />Ahorro promociones</span><span>-{dinero(cotizacion.descuento)}</span>
                    </div>
                  )}
                  <div style={{ fontSize: "2.4rem", fontWeight: 800, textAlign: "right", margin: "10px 0", color: "#0f172a" }}>
                    {cotizando ? <i className="fas fa-spinner fa-spin" style={{ fontSize: "1.4rem" }} /> : dinero(total)}
                  </div>
                  <Input placeholder="Cliente (opcional)" value={cliente} onChange={(e) => setCliente(e.target.value)} className="mb-2" />
                  <Button color="success" block size="lg" disabled={!carrito.length || !cotizacion || cotizando} onClick={abrirCobro} style={{ height: 58, fontSize: "1.15rem" }}>
                    <i className="fas fa-money-bill-wave mr-2" />Cobrar (F2)
                  </Button>
                  {carrito.length > 0 && (
                    <Button color="link" block className="text-muted" onClick={() => Swal.fire({ title: "¿Vaciar la venta?", icon: "question", showCancelButton: true, confirmButtonText: "Sí, vaciar", cancelButtonText: "No" }).then((r) => r.value && nuevaVenta())}>
                      Vaciar venta
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <VariantePicker
        articulo={picker}
        modo="venta"
        permiteSinExistencia={puedeSinEx}
        enCarrito={picker ? Object.fromEntries(carrito.filter((c) => c.articulos === picker._id).map((c) => [`${c.colores || ""}|${c.tallas || ""}`, c.cantidad])) : {}}
        onAgregar={async (lista) => { for (const v of lista) await agregarVariante(picker, v, v.cantidad); }}
        onCerrar={() => { setPicker(null); enfocar(); }}
      />

      {/* Cobro */}
      <Modal isOpen={modalCobro} toggle={() => setModalCobro(false)} size="md" onOpened={() => { const el = document.getElementById("pos-efectivo"); if (el) el.focus(); }}>
        <ModalHeader toggle={() => setModalCobro(false)}>Cobrar {dinero(total)}</ModalHeader>
        <ModalBody>
          <div className="mb-3" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Button color="success" size="sm" onClick={() => todoEn("efectivo")}><i className="fas fa-money-bill mr-1" />Todo efectivo</Button>
            <Button color="primary" size="sm" onClick={() => todoEn("tdc")}><i className="fas fa-credit-card mr-1" />Todo tarjeta</Button>
            <Button color="info" size="sm" onClick={() => todoEn("transfer")}><i className="fas fa-exchange-alt mr-1" />Todo transferencia</Button>
          </div>

          {[
            { k: "efectivo", label: "Efectivo recibido", icon: "fas fa-money-bill", color: "#10b981" },
            { k: "tdc", label: "Tarjeta (débito / crédito)", icon: "fas fa-credit-card", color: "#3b82f6", ref: "referenciaTdc" },
            { k: "transfer", label: "Transferencia", icon: "fas fa-exchange-alt", color: "#06b6d4", ref: "referenciaTransfer" },
          ].map((m) => (
            <div key={m.k} className="mb-3">
              <label className="mb-1"><i className={`${m.icon} mr-1`} style={{ color: m.color }} />{m.label}</label>
              <div style={{ display: "flex", gap: 6 }}>
                <Input
                  id={`pos-${m.k}`}
                  type="number" min="0" step="0.01" inputMode="decimal"
                  style={{ fontSize: "1.2rem", fontWeight: 700 }}
                  value={pago[m.k]}
                  onChange={(e) => setPago({ ...pago, [m.k]: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") cobrar(); }}
                />
                {falta > 0 && <Button size="sm" color="secondary" onClick={() => restoEn(m.k)} title="Poner lo que falta">Resto</Button>}
              </div>
              {m.ref && Number(pago[m.k]) > 0 && (
                <Input bsSize="sm" className="mt-1" placeholder="Referencia / últimos 4 dígitos (opcional)" value={pago[m.ref]} onChange={(e) => setPago({ ...pago, [m.ref]: e.target.value })} />
              )}
              {m.k === "efectivo" && (
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  {[100, 200, 500, 1000].map((b) => (
                    <Button key={b} size="sm" color="light" style={{ border: "1px solid #e2e8f0" }} onClick={() => setPago({ ...pago, efectivo: String((Number(pago.efectivo) || 0) + b) })}>+${b}</Button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.1rem", padding: "10px 14px", borderRadius: 10, background: falta > 0 ? "#fef2f2" : "#ecfdf5" }}>
            {falta > 0 ? (<><span>Falta</span><strong style={{ color: "#dc2626" }}>{dinero(falta)}</strong></>) : (<><span>Cambio</span><strong style={{ color: "#059669", fontSize: "1.5rem" }}>{dinero(cambio)}</strong></>)}
          </div>
          {errorPago && <div className="text-danger mt-2">{errorPago}</div>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalCobro(false)}>Regresar</Button>
          <Button color="success" size="lg" disabled={falta > 0 || !!errorPago || guardando} onClick={cobrar}>
            {guardando ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-check mr-1" />Registrar venta</>}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default PosVenta;
